import { useState } from 'react';
import type React from 'react';

import { Button, Link, Text } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core/react/i18n';
import { useKnownAvailableParams, useNavigate } from '@change-corgi/core/react/router';

import { useEndorsements } from '../../hooks';
import { EndorsementCard } from '../EndorsementCard';

import { DeleteEndorsementModal } from './components/DeleteEndorsementModal';
import {
	actionButtonsContainer,
	descriptionContainer,
	endorsementCardContainer,
	sectionContainer,
	titleContainer,
} from './EndorsementSection.styles';

export const EndorsementSection: React.FC = () => {
	const { slug } = useKnownAvailableParams<{ slug: string }>();
	const navigate = useNavigate();
	const [showSingleEndorsement, setShowSingleEndorsement] = useState(true);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [endorsementToDelete, setEndorsementToDelete] = useState<string | null>(null);

	const {
		data: { endorsements },
	} = useEndorsements({ petitionSlugOrId: slug });

	const handleAddAnother = () => {
		navigate(`/p/${slug}/dashboard/endorsements/add`);
	};

	const handleViewOnPetition = () => {
		// TODO: Navigate to petition page
		// eslint-disable-next-line no-console
		console.log('View on petition page clicked');
	};

	const handleEdit = () => {
		// TODO: Navigate to edit endorsement page
	};

	const handleRemove = (endorsementId: string) => {
		setEndorsementToDelete(endorsementId);
		setIsDeleteModalOpen(true);
	};

	const handleConfirmDelete = () => {
		// TODO: Actually delete the endorsement via API
		setEndorsementToDelete(endorsementToDelete);
	};

	const handleToggleEndorsementView = () => {
		setShowSingleEndorsement((prev) => !prev);
	};

	const endorsementsToDisplay = showSingleEndorsement ? endorsements.slice(0, 1) : endorsements;

	if (endorsements.length === 0) {
		return null;
	}

	return (
		<div className={sectionContainer()} data-testid="endorsement-section" id="endorsements">
			{/* Header */}
			<div className={titleContainer()}>
				<Text variant="heading" size="x-small" fontWeight="bold" className="text-typography-strong">
					<Translate value="corgi.petition.edit.endorsements.title" />
				</Text>
			</div>

			{/* Description */}
			<div className={descriptionContainer()}>
				<Text variant="body" className="text-typography-strong">
					<Translate value="corgi.petition.edit.endorsements.description" />{' '}
					<Link href="#" variant="secondary" className="underline">
						<Translate value="corgi.petition.edit.endorsements.see_example" />
					</Link>
				</Text>
			</div>

			{/* Endorsement Cards */}
			<div className={endorsementCardContainer()}>
				{endorsementsToDisplay.map((endorsement, index) => {
					const isFirstCard = index === 0;
					const readMoreLabel = showSingleEndorsement ? 'Read full endorsement' : 'View less';

					return (
						<EndorsementCard
							key={endorsement.id}
							name={endorsement.name}
							title={endorsement.title ?? ''}
							description={endorsement.content ?? ''}
							photoUrl={endorsement.photo?.userSmall.url ?? ''}
							link={endorsement.url ?? '#'}
							onEdit={handleEdit}
							onRemove={() => handleRemove(endorsement.id)}
							onReadMore={isFirstCard && endorsements.length > 1 ? handleToggleEndorsementView : undefined}
							readMoreLabel={isFirstCard && endorsements.length > 1 ? readMoreLabel : undefined}
							className="bg-background-base border-stroke-weak rounded-lg border"
						/>
					);
				})}
			</div>

			{/* Action Buttons */}
			<div className={actionButtonsContainer()}>
				<Button onClick={handleAddAnother} variant="secondary" data-testid="add-another-endorsement-button">
					<Translate value="corgi.petition.edit.endorsements.add_another_button" />
				</Button>
				<Button onClick={handleViewOnPetition} variant="tertiary" data-testid="view-on-petition-button">
					<Translate value="corgi.petition.edit.endorsements.view_on_petition_button" />
				</Button>
			</div>

			{/* Delete Confirmation Modal */}
			<DeleteEndorsementModal
				isOpen={isDeleteModalOpen}
				onOpenChange={setIsDeleteModalOpen}
				onConfirmDelete={handleConfirmDelete}
			/>
		</div>
	);
};
